import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

type Status = 'loading' | 'success' | 'already' | 'error';

const VerifyEmailPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const didFire = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation: we must consume the token exactly once.
    if (didFire.current) return;
    didFire.current = true;

    async function run() {
      if (!token) {
        setStatus('error');
        setMessage('Kein Verifizierungs-Token in der URL.');
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/auth/verify?token=${encodeURIComponent(token)}`
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setStatus('error');
          setMessage(body.error || 'Verifizierung fehlgeschlagen.');
          return;
        }
        setStatus(body.already ? 'already' : 'success');
      } catch {
        setStatus('error');
        setMessage('Netzwerkfehler beim Verifizieren.');
      }
    }

    run();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl p-8 text-center">
        <div className="flex items-center justify-center mb-4">
          <BookOpen className="w-10 h-10 text-blue-500" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-6">E-Mail-Verifizierung</h1>

        {status === 'loading' && (
          <div className="text-gray-300">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-blue-400 animate-spin" />
            <p>Token wird überprüft…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-gray-100">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-semibold mb-2">E-Mail bestätigt</p>
            <p className="text-gray-400 mb-6">
              Dein Konto ist jetzt aktiviert. Du kannst dich anmelden.
            </p>
            <Link
              to="/login"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition"
            >
              Zur Anmeldung
            </Link>
          </div>
        )}

        {status === 'already' && (
          <div className="text-gray-100">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-green-400" />
            <p className="text-lg font-semibold mb-2">Bereits bestätigt</p>
            <p className="text-gray-400 mb-6">Deine E-Mail war schon bestätigt.</p>
            <Link
              to="/login"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition"
            >
              Zur Anmeldung
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="text-gray-100">
            <XCircle className="w-14 h-14 mx-auto mb-4 text-red-400" />
            <p className="text-lg font-semibold mb-2">Verifizierung fehlgeschlagen</p>
            <p className="text-gray-400 mb-6">{message}</p>
            <Link
              to="/register"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition"
            >
              Neu registrieren
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
